import React, { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { animated, useSpring } from 'react-spring';
import useMeasure from 'react-use-measure';
import { ResizeObserver } from '@juggle/resize-observer';
import { Modal, ModalProps } from './Modal';

const AnimatedModal = animated(Modal);

export function ResponsiveModal({
  size = 'large',
  isOpen,
  ...props
}: { children: ReactNode } & ModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [modalMeasureRef, { height: modalHeight }] = useMeasure({
    polyfill: ResizeObserver,
  });
  const [modalHeaderMeasureRef, { height: modalHeaderHeight }] = useMeasure({
    polyfill: ResizeObserver,
  });

  const animatedHeight = useSpring({
    to: {
      height: isOpen ? modalHeight : 0,
    },
    from: {
      height: 0,
    },
  });

  useEffect(() => {
    setIsAnimating(true);
  }, [isOpen]);

  // 90% of the phones screen height
  const maxModalHeight = window.innerHeight * 0.97;
  const gapFromTop = window.innerHeight - maxModalHeight;

  const fadeAnimation = useSpring({
    to: {
      opacity: isOpen ? 1 : 0,
    },
    onRest: () => setIsAnimating(false),
  });

  const slideAnimation = useSpring({
    from: { top: isOpen ? `${maxModalHeight}px` : `${gapFromTop}px` },
    to: { top: isOpen ? `${gapFromTop}px` : `${maxModalHeight}px` },
    config: {
      duration: 0,
    },
  });

  // TODO figure out why the modal won't close
  if (!isOpen && !isAnimating) {
    return null;
  }

  const bodyMaxHeight = Math.round(
    // the largest possible modal height - the height of the header - 1px for the hr
    maxModalHeight - modalHeaderHeight - 1,
  );

  return (
    <AnimatedModal
      {...props}
      isOpen={isOpen}
      onDismiss={() => {
        props.onDismiss();
      }}
      className={classNames(
        'absolute bottom-0 max-w-full !mb-0 rounded-b-none sm:rounded-b-md',
        'sm:static sm:transition-none duration-500',
        props.className,
      )}
      size={size}
      style={{
        ...slideAnimation,
        ...props.style,
        ...{ maxHeight: maxModalHeight },
      }}
      bodyStyle={{ overflowY: 'scroll', maxHeight: `${bodyMaxHeight}px` }}
      bodyClassName="px-2 sm:px-12 pt-6 sm:pt-5 pb-0 sm:pb-12 text-sm text-gray-500 h-full"
      ref={modalHeaderMeasureRef}
    >
      <animated.div
        data-test-id="modal-content"
        style={animatedHeight}
        className={classNames(isAnimating && 'overflow-hidden', 'h-full')}
      >
        <animated.div style={fadeAnimation} ref={modalMeasureRef}>
          {/* keep body rendered until animation finishes */}
          {(isOpen || isAnimating) && props.children}
        </animated.div>
      </animated.div>
    </AnimatedModal>
  );
}
